import { useEffect, useLayoutEffect } from "react";
import { Outlet } from "react-router-dom";
import { publicPageShown } from "../../utils/pageSession";
import AdminSwitcher from "./AdminSwitcher";
import AdminHeader from "./AdminHeader";
import AdminSidebar from "./AdminSidebar";
import AdminSearchModal from "./AdminSearchModal";
import AdminFooter from "./AdminFooter";
import "../../Assets/css/AdminPanel.css";
import { AdminLangProvider } from "./i18n";

// Same stylesheets / scripts, in the same order, as the Xintra index.html.
const BOOTSTRAP_RTL = "/assets/libs/bootstrap/css/bootstrap.rtl.min.css";
const BOOTSTRAP_LTR = "/assets/libs/bootstrap/css/bootstrap.min.css";

const STYLESHEETS = [
  "/assets/css/styles.css",
  "/assets/css/icons.css",
  "/assets/libs/node-waves/waves.min.css",
  "/assets/libs/simplebar/simplebar.min.css",
  "/assets/libs/flatpickr/flatpickr.min.css",
  "/assets/libs/@simonwep/pickr/themes/nano.min.css",
  "/assets/libs/choices.js/public/assets/styles/choices.min.css",
  "/assets/libs/@tarekraafat/autocomplete.js/css/autoComplete.css",
];

const SCRIPTS = [
  "/assets/libs/choices.js/public/assets/scripts/choices.min.js",
  "/assets/js/main.js",
  "/assets/libs/@popperjs/core/umd/popper.min.js",
  "/assets/libs/bootstrap/js/bootstrap.bundle.min.js",
  "/assets/libs/node-waves/waves.min.js",
  "/assets/libs/simplebar/simplebar.min.js",
  "/assets/js/simplebar.js",
  "/assets/libs/@tarekraafat/autocomplete.js/autoComplete.min.js",
  "/assets/libs/@simonwep/pickr/pickr.es5.min.js",
  "/assets/libs/flatpickr/flatpickr.min.js",
  "/assets/js/sticky.js",
  "/assets/js/defaultmenu.min.js",
  "/assets/js/custom.js",
  "/assets/js/custom-switcher.min.js",
];

// The <aside id="sidebar"> the template scripts were run against. The scripts
// declare top-level consts, so they can only run once per page load.
let boundSidebar: Element | null = null;

function savedDirection(): "ltr" | "rtl" {
  try {
    return localStorage.getItem("xintraltr") ? "ltr" : "rtl";
  } catch {
    return "rtl";
  }
}

function addStylesheet(href: string, id?: string): Promise<void> {
  if (document.querySelector(`link[data-admin="${href}"]`)) return Promise.resolve();
  return new Promise((resolve) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute("data-admin", href);
    if (id) link.id = id;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => {
      console.error(`خطا در بارگذاری اسکریپت: ${src}`);
      resolve();
    };
    document.body.appendChild(script);
  });
}

export default function AdminLayout() {
  // the admin panel must never be indexed (robots.txt blocks it as well)
  useEffect(() => {
    document.querySelector('meta[name="robots"]')?.setAttribute("content", "noindex, nofollow, noarchive");
  }, []);

  // <html> attributes of the template, set before the first paint
  useLayoutEffect(() => {
    if (boundSidebar) return;
    const html = document.documentElement;
    html.setAttribute("dir", savedDirection());
    html.setAttribute("data-nav-layout", "vertical");
    html.setAttribute("data-theme-mode", "light");
    html.setAttribute("data-header-styles", "light");
    html.setAttribute("data-menu-styles", "dark");
    html.setAttribute("data-toggled", "close");
    document.body.style.visibility = "hidden"; // until the template CSS / JS is ready
  }, []);

  useEffect(() => {
    // came here from the public site in the same page load -> start clean
    if (publicPageShown()) {
      window.location.reload();
      return;
    }

    const sidebar = document.getElementById("sidebar");
    if (boundSidebar && boundSidebar !== sidebar) {
      window.location.reload();
      return;
    }

    if (!boundSidebar) {
      boundSidebar = sidebar;
      (async () => {
        const bootstrapHref = savedDirection() === "ltr" ? BOOTSTRAP_LTR : BOOTSTRAP_RTL;
        // id="style" is the link the template swaps when switching RTL / LTR
        await Promise.all([addStylesheet(bootstrapHref, "style"), ...STYLESHEETS.map((href) => addStylesheet(href))]);
        for (const src of SCRIPTS) await loadScript(src);
        // custom.js hides the loader on window "load", which may already have fired
        if (document.readyState === "complete") document.getElementById("loader")?.classList.add("d-none");
        document.body.style.visibility = "";
      })();
    }

    return () => {
      // Leaving the admin panel (not React StrictMode's test re-mount, which keeps
      // the DOM): reload so the template scripts / styles don't stay on the page.
      setTimeout(() => {
        if (!document.getElementById("sidebar")) window.location.reload();
      }, 0);
    };
  }, []);

  return (
    <AdminLangProvider>
      <AdminSwitcher />

      <div id="loader">
        <img src="/assets/images/media/loader.svg" alt="" />
      </div>

      <div className="page">
        <AdminHeader />
        <AdminSidebar />

        <div className="main-content app-content">
          <div className="container-fluid">
            <Outlet />
          </div>
        </div>

        <AdminSearchModal />
        <AdminFooter />
      </div>

      <div className="scrollToTop">
        <span className="arrow">
          <i className="ti ti-arrow-narrow-up fs-20"></i>
        </span>
      </div>
      <div id="responsive-overlay"></div>
    </AdminLangProvider>
  );
}
